import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  CalendarDays, Plus, Loader2, Trash2, Clock, Repeat, AlertTriangle,
  CalendarOff, MapPinned, Briefcase, GraduationCap, Plane,
} from 'lucide-react';
import api from '../../utils/api';

const STATUS_META = {
  available:       { label: 'Available',          icon: CalendarDays, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  unavailable:     { label: 'Unavailable',        icon: CalendarOff,  cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  tentative:       { label: 'Tentative',          icon: AlertTriangle, cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  booked_external: { label: 'Booked elsewhere',   icon: Briefcase,    cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  out_of_market:   { label: 'Out of market',      icon: MapPinned,    cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-700 border-gray-200', icon: CalendarDays };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-semibold border ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

const initialBlock = () => ({
  start_date: '',
  end_date: '',
  start_time: '',
  end_time: '',
  status: 'unavailable',
  reason_code: '',
  reason_note: '',
  reason: '',
});

const initialRule = () => ({
  day_of_week: 1,
  start_time: '',
  end_time: '',
  status: 'unavailable',
});

const REASON_PRESETS = [
  { value: 'booked_other_agency', label: 'Booked with another agency' },
  { value: 'traveling',           label: 'Traveling' },
  { value: 'vacation',            label: 'Vacation' },
  { value: 'school',              label: 'School' },
  { value: 'full_time_job',       label: 'Full-time job' },
  { value: 'personal_conflict',   label: 'Personal conflict' },
  { value: 'out_of_market',       label: 'Out of market' },
  { value: 'not_available',       label: 'Not available that day' },
  { value: 'other',               label: 'Other' },
];

function reasonLabelFor(value) {
  return REASON_PRESETS.find(o => o.value === value)?.label || '';
}

export default function TalentAvailability() {
  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [rules, setRules] = useState([]);
  const [blockForm, setBlockForm] = useState(initialBlock());
  const [ruleForm, setRuleForm] = useState(initialRule());
  const [savingBlock, setSavingBlock] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get('/talent/availability/my');
      setBlocks(r.data.blocks || []);
      setRules(r.data.recurring || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load availability');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const submitBlock = async () => {
    if (!blockForm.start_date || !blockForm.end_date) {
      toast.error('Pick both start and end dates');
      return;
    }
    setSavingBlock(true);
    try {
      const payload = { ...blockForm };
      if (!payload.start_time) delete payload.start_time;
      if (!payload.end_time) delete payload.end_time;
      if (!payload.reason) delete payload.reason;
      await api.post('/talent/availability/blocks', payload);
      toast.success('Block added');
      setBlockDialogOpen(false);
      setBlockForm(initialBlock());
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add block');
    } finally { setSavingBlock(false); }
  };

  const submitRule = async () => {
    setSavingRule(true);
    try {
      const payload = { ...ruleForm };
      if (!payload.start_time) delete payload.start_time;
      if (!payload.end_time) delete payload.end_time;
      await api.post('/talent/availability/recurring', payload);
      toast.success('Recurring rule added');
      setRuleDialogOpen(false);
      setRuleForm(initialRule());
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add rule');
    } finally { setSavingRule(false); }
  };

  const deleteBlock = async (id) => {
    if (!window.confirm('Delete this availability block?')) return;
    try {
      await api.delete(`/talent/availability/blocks/${id}`);
      toast.success('Block deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this recurring rule?')) return;
    try {
      await api.delete(`/talent/availability/recurring/${id}`);
      toast.success('Rule deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid="talent-availability-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-6 h-6 text-cyan-600" /> My Availability
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Tell us when you can't take a gig. We use this to avoid sending you invites you'd have to decline anyway.
        </p>
      </div>

      {/* Blocks */}
      <Card data-testid="availability-blocks-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CalendarOff className="w-4 h-4 text-rose-500" /> Date Blocks
          </CardTitle>
          <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-block-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Block
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add an availability block</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Start date</Label>
                    <Input type="date" data-testid="block-start-date"
                      value={blockForm.start_date}
                      onChange={e => setBlockForm({ ...blockForm, start_date: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">End date</Label>
                    <Input type="date" data-testid="block-end-date"
                      value={blockForm.end_date}
                      onChange={e => setBlockForm({ ...blockForm, end_date: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Start time (opt.)</Label>
                    <Input type="time" data-testid="block-start-time"
                      value={blockForm.start_time}
                      onChange={e => setBlockForm({ ...blockForm, start_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">End time (opt.)</Label>
                    <Input type="time" data-testid="block-end-time"
                      value={blockForm.end_time}
                      onChange={e => setBlockForm({ ...blockForm, end_time: e.target.value })} />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 -mt-2">Leave times empty for an all-day block.</p>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={blockForm.status} onValueChange={(v) => setBlockForm({ ...blockForm, status: v })}>
                    <SelectTrigger data-testid="block-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).filter(([k]) => k !== 'available').map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Reason</Label>
                  <Select
                    value={blockForm.reason_code}
                    onValueChange={(v) => setBlockForm({
                      ...blockForm,
                      reason_code: v,
                      reason: v === 'other' ? (blockForm.reason_note || '') : reasonLabelFor(v),
                    })}
                  >
                    <SelectTrigger data-testid="block-reason-select">
                      <SelectValue placeholder="Pick a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      {REASON_PRESETS.map(p => (
                        <SelectItem key={p.value} value={p.value} data-testid={`block-reason-${p.value}`}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {blockForm.reason_code === 'other' && (
                    <Input
                      className="mt-2"
                      data-testid="block-reason-other-note"
                      placeholder="Add a short note…"
                      value={blockForm.reason_note}
                      onChange={e => setBlockForm({
                        ...blockForm,
                        reason_note: e.target.value,
                        reason: e.target.value || 'Other',
                      })}
                    />
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBlockDialogOpen(false)} disabled={savingBlock}>Cancel</Button>
                <Button onClick={submitBlock} disabled={savingBlock} data-testid="submit-block">
                  {savingBlock ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Block'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : blocks.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4" data-testid="blocks-empty">
              No date blocks. Add one to mark vacation, conflicting bookings, or any day you can't work.
            </p>
          ) : (
            <div className="space-y-1.5" data-testid="blocks-list">
              {blocks.map(b => (
                <div key={b.id} data-testid={`block-${b.id}`} className="border border-gray-100 rounded-md p-2.5 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">
                        {b.start_date}{b.start_date !== b.end_date && ` → ${b.end_date}`}
                      </span>
                      <StatusPill status={b.status} />
                      {b.start_time && b.end_time && (
                        <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-gray-100 text-gray-600 border-0">
                          <Clock className="w-3 h-3 mr-0.5" />{b.start_time}–{b.end_time}
                        </Badge>
                      )}
                    </div>
                    {b.reason && <p className="text-xs text-gray-500 mt-0.5">{b.reason}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteBlock(b.id)} data-testid={`delete-block-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recurring */}
      <Card data-testid="availability-recurring-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Repeat className="w-4 h-4 text-cyan-500" /> Weekly Recurring Schedule
          </CardTitle>
          <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid="add-rule-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add a recurring weekly rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Day of week</Label>
                  <Select value={String(ruleForm.day_of_week)} onValueChange={(v) => setRuleForm({ ...ruleForm, day_of_week: Number(v) })}>
                    <SelectTrigger data-testid="rule-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAY_NAMES.map((n, i) => <SelectItem key={i} value={String(i)}>{['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][i]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Start time (opt.)</Label>
                    <Input type="time" data-testid="rule-start-time" value={ruleForm.start_time}
                      onChange={e => setRuleForm({ ...ruleForm, start_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">End time (opt.)</Label>
                    <Input type="time" data-testid="rule-end-time" value={ruleForm.end_time}
                      onChange={e => setRuleForm({ ...ruleForm, end_time: e.target.value })} />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 -mt-2">
                  Leave times empty for an all-day rule. Example: "Mon–Fri after 5pm" → create 5 rules,
                  status=Available, time 17:00–23:59. Or "Not available mornings" → 5 rules, status=Unavailable, 00:00–12:00.
                </p>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={ruleForm.status} onValueChange={(v) => setRuleForm({ ...ruleForm, status: v })}>
                    <SelectTrigger data-testid="rule-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRuleDialogOpen(false)} disabled={savingRule}>Cancel</Button>
                <Button onClick={submitRule} disabled={savingRule} data-testid="submit-rule">
                  {savingRule ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Rule'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : rules.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4" data-testid="rules-empty">
              No recurring rules. Use these to mark "Mon–Fri after 5pm" or "Weekends only" patterns.
            </p>
          ) : (
            <div className="space-y-1.5" data-testid="rules-list">
              {rules.map(r => (
                <div key={r.id} data-testid={`rule-${r.id}`} className="border border-gray-100 rounded-md p-2.5 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">{DAY_NAMES[r.day_of_week]}</span>
                      <StatusPill status={r.status} />
                      {r.start_time && r.end_time && (
                        <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-gray-100 text-gray-600 border-0">
                          <Clock className="w-3 h-3 mr-0.5" />{r.start_time}–{r.end_time}
                        </Badge>
                      )}
                      {!r.start_time && (
                        <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-gray-100 text-gray-600 border-0">All day</Badge>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteRule(r.id)} data-testid={`delete-rule-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-cyan-50 to-white border-cyan-100">
        <CardContent className="p-4 text-xs text-cyan-900 flex items-start gap-2">
          <CalendarDays className="w-4 h-4 mt-0.5 shrink-0 text-cyan-600" />
          <div>
            <p className="font-semibold">Coming soon</p>
            <p className="mt-0.5">
              Google Calendar · Outlook · Apple Calendar — we'll sync your existing calendars
              automatically so you don't have to mark blocks manually.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
